import axios from 'axios';
// config
import { HOST_API } from '../config';

// ----------------------------------------------------------------------

const DEVICE_ID_STORAGE_KEY = "mfa-device-id";

let cachedDeviceId: string | null = null;
let pendingDeviceIdPromise: Promise<string> | null = null;

const getBrowserName = () => {
  if (typeof navigator === "undefined") return "Unknown";

  const ua = navigator.userAgent;

  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("OPR/") || ua.includes("Opera")) return "Opera";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/")) return "Safari";

  return "Unknown";
};

const toSha256Hex = async (value: string) => {
  if (typeof window !== "undefined" && window.crypto?.subtle && window.TextEncoder) {
    const data = new TextEncoder().encode(value);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  return value;
};

const buildRawDeviceSeed = () => {
  const randomPart =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Math.random()}-${Date.now()}`;

  return `${randomPart}-${Date.now()}`;
};

const createDeviceId = async () => {
  const rawSeed = buildRawDeviceSeed();
  return toSha256Hex(rawSeed);
};

const getDeviceId = async () => {
  if (cachedDeviceId) return cachedDeviceId;

  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (stored) {
      cachedDeviceId = stored;
      return stored;
    }
  }

  if (!pendingDeviceIdPromise) {
    pendingDeviceIdPromise = createDeviceId().then((deviceId) => {
      cachedDeviceId = deviceId;

      if (typeof window !== "undefined") {
        localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
      }

      pendingDeviceIdPromise = null;
      return deviceId;
    });
  }

  return pendingDeviceIdPromise;
};

const axiosInstance = axios.create({
  baseURL: HOST_API,
});

axiosInstance.interceptors.request.use(async (config) => {
  const deviceId = await getDeviceId();

  config.headers = {
    ...(config.headers ?? {}),
    "device-id": deviceId,
    "device-name": getBrowserName(),
    "device-platform": "WEB",
  };

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;


